import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Bug, Loader2 } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { api } from "@/lib/api";

const inputCls = "w-full rounded-md border border-line bg-bg px-3 py-2.5 text-sm text-ink-primary placeholder:text-ink-muted focus:outline-none focus:border-brand-amber/60";
const Field = ({ label, required, children }) => (
  <div>
    <div className="font-mono text-[10px] uppercase tracking-wider text-ink-muted mb-1.5">
      {label}{required && <span className="text-brand-amber ml-0.5">*</span>}
    </div>
    {children}
  </div>
);

const FREQUENCIES = [
  { v: "once", l: "Happened once" }, { v: "occasionally", l: "Happens occasionally" },
  { v: "always", l: "Happens every time" }, { v: "unknown", l: "Unable to determine" },
];
const SEVERITIES = [
  { v: "low", l: "Low — cosmetic / minor" }, { v: "medium", l: "Medium — annoying but workable" },
  { v: "high", l: "High — blocks a feature" }, { v: "critical", l: "Critical — nothing works" },
];

function detectClient() {
  const ua = navigator.userAgent;
  return {
    browser: /edg\//i.test(ua) ? "Edge" : /chrome|crios/i.test(ua) ? "Chrome" : /firefox/i.test(ua) ? "Firefox" : /safari/i.test(ua) ? "Safari" : "Other",
    device_type: /mobi|android|iphone|ipad/i.test(ua) ? "Mobile" : "Desktop",
    os: /windows/i.test(ua) ? "Windows" : /mac os/i.test(ua) ? "macOS" : /android/i.test(ua) ? "Android" : /iphone|ipad/i.test(ua) ? "iOS" : /linux/i.test(ua) ? "Linux" : "Other",
  };
}

export default function SupportBugReport() {
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [busy, setBusy] = useState(false);
  const [f, setF] = useState({
    title: "", expected: "", actual: "", steps: "", frequency: "unknown",
    station_id: "", affected_feature: "", severity: "medium", allow_logs: true,
  });
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  useEffect(() => { api.get("/helpdesk/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const submit = async () => {
    if (!f.title.trim()) { toast.error("Give the bug a title"); return; }
    if (f.expected.trim().length < 5 || f.actual.trim().length < 5) {
      toast.error("Fill in what you expected and what actually happened"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/helpdesk/bug-reports", { ...f, ...detectClient() });
      toast.success(`Bug report ${data.reference} filed — you can attach a screenshot on the ticket`);
      navigate(`/dashboard/support/tickets/${data.reference}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't file the bug report"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-6" data-testid="bug-report-page">
      <Link to="/dashboard/help" className="inline-flex items-center gap-1.5 text-xs text-ink-muted hover:text-ink-primary">
        <ArrowLeft className="h-3.5 w-3.5" /> Help Center
      </Link>
      <PageHeader eyebrow="// REPORT A PROBLEM" title="Report a bug"
        description="Structured bug reports get diagnosed fastest — screenshots can be attached on the ticket after submitting." />
      <div className="rounded-xl border border-line bg-bg-surface p-5 space-y-4">
        <Field label="Bug title" required>
          <input className={inputCls} value={f.title} onChange={(e) => set("title")(e.target.value)}
            placeholder="e.g. Now Playing card renders without artwork" data-testid="bug-title" />
        </Field>
        <Field label="What did you expect to happen?" required>
          <textarea className={`${inputCls} min-h-[70px]`} value={f.expected}
            onChange={(e) => set("expected")(e.target.value)} data-testid="bug-expected" />
        </Field>
        <Field label="What actually happened?" required>
          <textarea className={`${inputCls} min-h-[70px]`} value={f.actual}
            onChange={(e) => set("actual")(e.target.value)} data-testid="bug-actual" />
        </Field>
        <Field label="Steps to reproduce">
          <textarea className={`${inputCls} min-h-[90px]`} value={f.steps}
            onChange={(e) => set("steps")(e.target.value)} data-testid="bug-steps"
            placeholder={"1. Open Now Playing\n2. Enable auto posts\n3. Wait for the next song…"} />
        </Field>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="How often does it happen?">
            <select className={inputCls} value={f.frequency} onChange={(e) => set("frequency")(e.target.value)} data-testid="bug-frequency">
              {FREQUENCIES.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
            </select>
          </Field>
          <Field label="Severity">
            <select className={inputCls} value={f.severity} onChange={(e) => set("severity")(e.target.value)} data-testid="bug-severity">
              {SEVERITIES.map((x) => <option key={x.v} value={x.v}>{x.l}</option>)}
            </select>
          </Field>
          <Field label="Affected station">
            <select className={inputCls} value={f.station_id} onChange={(e) => set("station_id")(e.target.value)} data-testid="bug-station">
              <option value="">Not station-specific</option>
              {(meta?.stations || []).map((s) => <option key={s.station_id} value={s.station_id}>{s.name}</option>)}
            </select>
          </Field>
          <Field label="Affected feature">
            <select className={inputCls} value={f.affected_feature} onChange={(e) => set("affected_feature")(e.target.value)} data-testid="bug-feature">
              <option value="">Choose…</option>
              {(meta?.features || []).map((x) => <option key={x} value={x}>{x}</option>)}
            </select>
          </Field>
        </div>
        <label className="flex items-start gap-2.5 cursor-pointer" data-testid="bug-allow-logs">
          <input type="checkbox" checked={f.allow_logs} onChange={(e) => set("allow_logs")(e.target.checked)} className="mt-0.5 accent-[#FF8A1F]" />
          <span className="text-xs text-ink-secondary">Support may examine my relevant activity logs (credentials always redacted). A diagnostic snapshot — your plan, station, connected integrations and recent errors — is attached automatically to speed up triage.</span>
        </label>
        <div className="flex justify-end pt-2 border-t border-line">
          <button type="button" onClick={submit} disabled={busy} data-testid="bug-submit-btn"
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-5 py-2.5 text-sm font-semibold text-black hover:bg-brand-amber/90 transition disabled:opacity-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bug className="h-4 w-4" />} File bug report
          </button>
        </div>
      </div>
    </div>
  );
}
